import { Component } from '@theme/component';
import { cleanText, generalInquiryMailto } from '@theme/project-utils';

/**
 * @typedef {Object} GeneralInquiryRefs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} email
 * @property {HTMLTextAreaElement} message
 * @property {HTMLButtonElement} emailButton
 * @property {HTMLAnchorElement} draftLink
 * @property {HTMLElement} status
 */

/** Customer-originated drafts with an honest native Shopify fallback. @extends {Component<GeneralInquiryRefs>} */
class GeneralInquiry extends Component {
  requiredRefs = ['form', 'email', 'message', 'emailButton', 'draftLink', 'status'];
  #abort = new AbortController();

  connectedCallback() { super.connectedCallback(); this.#setup(); }
  updatedCallback() { super.updatedCallback(); this.#setup(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#abort.abort(); this.refs.draftLink?.removeAttribute('href'); }

  #setup() {
    this.#abort.abort();
    this.#abort = new AbortController();
    this.refs.emailButton.hidden = false;
    this.refs.form.addEventListener('input', () => {
      this.refs.email.setCustomValidity('');
      this.refs.message.setCustomValidity('');
      this.refs.status.textContent = '';
      this.refs.draftLink.hidden = true;
      this.refs.draftLink.removeAttribute('href');
    }, {signal: this.#abort.signal});
    const validateOnline = (event) => {
      this.refs.email.setCustomValidity('');
      if (!this.refs.email.value.trim()) {
        this.refs.email.setCustomValidity(this.dataset.onlineEmailRequired);
        this.refs.status.textContent = this.dataset.onlineEmailRequired;
      }
      if (!this.#validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    // Reject incomplete online requests at click time, before Shopify's CAPTCHA handler.
    this.refs.form.querySelector('button[type="submit"]')?.addEventListener('click', validateOnline, {capture: true, signal: this.#abort.signal});
    this.refs.form.addEventListener('submit', validateOnline, {capture: true, signal: this.#abort.signal});
  }

  #validate() {
    this.refs.message.setCustomValidity(cleanText(this.refs.message.value, true) ? '' : this.dataset.messageRequired);
    return this.refs.form.reportValidity();
  }

  /** @param {MouseEvent} event - Opens a draft; never asserts delivery. */
  openEmailDraft(event) {
    event.preventDefault();
    this.refs.email.setCustomValidity('');
    if (!this.#validate()) return;
    const fields = new FormData(this.refs.form);
    this.refs.draftLink.href = generalInquiryMailto({name: fields.get('contact[name]'), email: fields.get('contact[email]'), message: fields.get('contact[body]')});
    this.refs.draftLink.hidden = false;
    this.refs.status.textContent = this.dataset.draftReady;
    this.refs.draftLink.click();
  }
}
if (!customElements.get('general-inquiry')) customElements.define('general-inquiry', GeneralInquiry);
